import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import images from "../common/images";
import Layout from "../components/Layout";
import Banner from "../components/Banner";
import EventCard from "../components/EventCard";
import ReactWOW from "react-wow";
import CityDropDown from "../components/CityDropDown";
import { useSelector } from "react-redux";
import FavouriteIcon from "../components/FavouriteIcon";
import moment from "moment";
import { formatTimeRange } from "../utils/common";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Hero from "../components/Hero";
import toast from "react-hot-toast";
import HeroCtaForm from "../components/HeroCtaForm";
import PageSeoContent from "../components/PageSeoContent";
import EventSubmitForm from "../components/EventSubmitForm";
import EventsCarousel from "../components/EventsCarousel";
import ClubsCarousel from "../components/ClubsCarousel";
import ArtistsCarousel from "../components/ArtistsCarousel";
import EventsGallery from "../components/EventsGallery";
import ImageWithSkeleton from "../components/ImageWithSkeleton";
import ErrorBoundary from "../components/ErrorBoundary";
import listEventCtaMobileImg from '../assets/img/listevent_cta_mobile.svg'
import listEventCtaImg from '../assets/img/listevent_cta.svg'
import useUserLocation from "../hooks/useUserLocation";
import { resolveApiCity, resolveUserBaseCity } from "../utils/city";
import ApiCall from "../services/apiCall";
import { filterItemsWithinRadius, sortByLocation } from "../utils/locationSort";


export function useAutoPlayVideo() {
   const videoRef = useRef(null);

   useEffect(() => {
      const video = videoRef.current;
      if (video) {
         video.muted = true; // autoplay requires muted
         video.loop = true;
         const playPromise = video.play();
         if (playPromise !== undefined) {
            playPromise.catch((err) =>
               console.warn("Autoplay failed:", err.message)
            );
         }
      }
   }, []);

   return videoRef;
}

export function useMediaQuery(query) {
   const [matches, setMatches] = useState(() =>
      typeof window !== "undefined" ? window.matchMedia(query).matches : false
   );

   useEffect(() => {
      const media = window.matchMedia(query);
      const listener = () => setMatches(media.matches);
      listener(); // initial check

      media.addEventListener("change", listener);
      return () => media.removeEventListener("change", listener);
   }, [query]);

   return matches;
}

const VideoBanner = ({ children, className = '' }) => {
   const videoRef = useAutoPlayVideo();

   return (
      <video
         ref={videoRef}
         playsInline
         muted
         loop
         className={className || "w-full w-100 rounded"}
      >
         {children}
      </video>
   );
}

const SkeletonCard = ({
   width = 300,
   height = 180,
   borderRadius = 12,
   showAvatar = true,
   lines = 3,
}) => {
   return (
      <div
         style={{
            width,
            padding: 16,
            borderRadius,
            border: "1px solid #ddd",
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
         }}
      >
         {showAvatar && (
            <div
               style={{ display: "flex", alignItems: "center", marginBottom: 12 }}
            >
               <Skeleton circle height={40} width={40} />
               <div style={{ marginLeft: 12, flex: 1 }}>
                  <Skeleton width="80%" height={12} />
                  <Skeleton width="50%" height={10} style={{ marginTop: 4 }} />
               </div>
            </div>
         )}
         <Skeleton height={150} borderRadius={8} />
         <div style={{ marginTop: 12 }}>
            {[...Array(lines)].map((_, i) => (
               <Skeleton key={i} height={10} style={{ marginTop: 6 }} />
            ))}
         </div>
      </div>
   );
};

const LocationRetryPrompt = ({
   isLoadingLocation = false,
   permissionDenied = false,
   locationError = null,
   onRetryLocation,
}) => (
   <div className="location-retry-panel">
      <div className="location-retry-icon">
         <i className="fa fa-map-marker" aria-hidden="true" />
      </div>
      <h3>Live Location Required</h3>
      <p className="location-retry-copy">
         To discover clubs near you, we need access to your real-time location. The nearby clubs section uses your live coordinates to show venues around you.
      </p>
      {isLoadingLocation ? (
         <p className="location-retry-message location-retry-message--muted">
            Requesting your current location from the browser...
         </p>
      ) : permissionDenied ? (
         <p className="location-retry-message">
            Your browser has blocked location access. Please allow location permission from the browser address bar or settings, then retry.
         </p>
      ) : locationError ? (
         <p className="location-retry-message">{locationError}</p>
      ) : null}

      <button
         type="button"
         className="btn btn-primary location-retry-button"
         onClick={onRetryLocation}
         disabled={isLoadingLocation}
      >
         <i className="fa fa-location-arrow" aria-hidden="true" />
         {isLoadingLocation
            ? 'Requesting Location...'
            : permissionDenied
               ? 'Retry Location Access'
               : 'Enable Location Access'}
      </button>
   </div>
);

const options = {
   stagePadding: 50,
   loop: true,
   margin: 10,
   nav: true,
   navText: [
      '<i class="fa fa-angle-left" aria-hidden="true"></i>',
      '<i class="fa fa-angle-right" aria-hidden="true"></i>',
   ],
   navContainer: "#club-spotlight-slider .custom-nav",
   responsive: {
      0: {
         items: 1,
      },
      600: {
         items: 2,
      },
      1000: {
         items: 4,
      },
      1300: {
         items: 5,
      },
   },
};
const options1 = {
   stagePadding: 50,
   loop: true,
   margin: 10,
   nav: true,
   navText: [
      '<i class="fa fa-angle-left" aria-hidden="true"></i>',
      '<i class="fa fa-angle-right" aria-hidden="true"></i>',
   ],
   navContainer: "#club-nearby-slider .custom-nav",
   responsive: {
      0: {
         items: 1,
      },
      600: {
         items: 2,
      },
      1000: {
         items: 4,
      },
      1300: {
         items: 5,
      },
   },
};
const options2 = {
   stagePadding: 50,
   loop: true,
   margin: 10,
   nav: true,
   navText: [
      '<i class="fa fa-angle-left" aria-hidden="true"></i>',
      '<i class="fa fa-angle-right" aria-hidden="true"></i>',
   ],
   navContainer: "#club-aritst-slider .custom-nav",
   responsive: {
      0: {
         items: 1,
      },
      600: {
         items: 2,
      },
      1000: {
         items: 4,
      },
      1300: {
         items: 5,
      },
   },
};
const options3 = {
   stagePadding: 50,
   // loop: true,
   margin: 10,
   nav: true,
   navText: [
      '<i class="fa fa-angle-left" aria-hidden="true"></i>',
      '<i class="fa fa-angle-right" aria-hidden="true"></i>',
   ],
   navContainer: "#club-events-slider .custom-nav",
   responsive: {
      0: {
         items: 1,
      },
      600: {
         items: 2,
      },
      1000: {
         items: 4,
      },
      1300: {
         items: 5,
      },
   },
};

const NearbyClubsLoadingSkeleton = () => (
   <div className="nearby-clubs-loading">
      <div className="nearby-clubs-loading__spinner">
         <div className="nearby-clubs-loading__dot-pulse">
            <span></span><span></span><span></span>
         </div>
      </div>
      <p className="nearby-clubs-loading__text">
         Fetching nearby clubs based on your location…
      </p>
   </div>
);

const ClubsSection = ({
   title,
   view,
   coords,
   city = null,
   baseCity = null,
   locationData = null,
   locationStatus = "ready",
   locationPermission = null,
   locationLoading = false,
   locationError = null,
   onRetryLocation = null,
}) => {
   const hasUserCoordinates =
      Number.isFinite(coords?.latitude) && Number.isFinite(coords?.longitude);
   const isLoadingLocation = locationLoading && !hasUserCoordinates;
   const permissionDenied = locationPermission === "denied";

   // Nearby clubs need live GPS. Never fall back to city clubs.
   const showRetryPrompt = !hasUserCoordinates && !isLoadingLocation;

   const getClubsFromResponse = (response) => {
      const clubs =
         response?.results?.clubs ||
         response?.data?.clubs ||
         response?.clubs ||
         response?.data ||
         [];

      return Array.isArray(clubs) ? clubs : [];
   };

   const fetchNearbyClubs = async () => {
      if (!hasUserCoordinates) return [];

      const userCoordinates = {
         latitude: Number(coords.latitude),
         longitude: Number(coords.longitude),
      };
      const locationPoint = {
         type: "Point",
         coordinates: [userCoordinates.longitude, userCoordinates.latitude],
      };

      const params = new URLSearchParams();
      params.append("view", "clubs");
      params.append("page", "0");
      params.append("location", JSON.stringify(locationPoint));
      params.append("coordinates", locationPoint.coordinates.join(","));

      const response = await ApiCall(`api/clubs?${params.toString()}`, "GET");
      if (response?.ok === false) {
         throw new Error("Failed to fetch nearby clubs");
      }

      const clubs = getClubsFromResponse(response);
      const nearbyClubs = filterItemsWithinRadius(clubs, userCoordinates);

      return sortByLocation(nearbyClubs, { coordinates: userCoordinates });
   };

   const {
      data: clubsNearbyData,
      error: clubsNearbyDataError,
      isLoading: clubsNearbyDataIsLoading,
      isFetching: clubsNearbyDataIsFetching,
      error: clubsNearbyDataIsError,
      refetch: clubsNearbyRefetch,
   } = useQuery({
      queryKey: [
         "nearby-clubs",
         coords?.latitude,
         coords?.longitude,
      ],
      queryFn: fetchNearbyClubs,
      placeholderData: [],
      enabled: hasUserCoordinates,
   });

   const isLoadingNearbyClubs =
      clubsNearbyDataIsLoading || clubsNearbyDataIsFetching;
   const loadingPlaceholders = Array.from({ length: 4 }, (_, idx) => ({
      placeholder: true,
      _id: `placeholder-${idx}`,
   }));

   // Show retry prompt only on hard failures (denied/error) – not while loading
   if (showRetryPrompt && onRetryLocation) {
      return (
         <div className="section-gap location-retry-section">
            <div className="container">
               <LocationRetryPrompt
                  isLoadingLocation={false}
                  permissionDenied={permissionDenied}
                  locationError={locationError}
                  onRetryLocation={onRetryLocation}
               />
            </div>
         </div>
      );
   }

   return (
      <>
         {/* Club-Sportlight Start */}
         <div
            className="club-spotlight head-title section-gap wow fadeInUp"
            data-wow-delay="0.2s"
         >
            <div className="container">
               <div className="mb-4 title-sec wow fadeInUp" data-wow-delay="0.3s">
                  <h2 className="title-25">{title}</h2>
               </div>
               <div className="px-3 mb-2 text-center wow fadeInUp" data-wow-delay="0.4s">
                  <strong>Find the Hottest Venues Around You.</strong>
               </div>
               <div className="px-3 text-center wow fadeInUp" data-wow-delay="0.4s">
                  Looking for a night out that's just around the corner? Explore top-rated clubs near your current location — sorted for convenience, handpicked for experience. Wherever you are, the party is never far.
               </div>
            </div>
            <div className="container">
               <div className="container__content">
                  <div className="club-items wow fadeInUp" data-wow-delay="0.5s">
                     <div id="club-nearby-slider" className="club-result-row itmes-slider">
                        {isLoadingLocation || isLoadingNearbyClubs ? (
                           <NearbyClubsLoadingSkeleton />
                        ) : clubsNearbyData?.length > 0 ? (
                           <>
                              <ErrorBoundary>
                                 <ClubsCarousel data={clubsNearbyData} />
                              </ErrorBoundary>
                           </>
                        ) : (
                           <div className="no-data">
                              <p className="no-data-found">No clubs found</p>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>
         </div>
         {/* Club-Sportlight End */}
      </>
   )
}

export default function Home() {
   // geo location data
   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);
   const selectedCity = useSelector((state) => state.citySelector.selectedCity);
   const userBaseCity = useSelector((state) => state.citySelector.userBaseCity);
   const isManualCitySelection = useSelector((state) => state.citySelector.isManualSelection);
   const [eventModalOpen, setEventModalOpen] = useState(false);
   const isMobile = useMediaQuery("(max-width: 768px)");
   // const [artistsInSpotlight, setArtistsInSpotlight] = useState({});
   const {
      coords,
      locationData,
      locationStatus,
      permissionState,
      loading: locationLoading,
      error: locationError,
      refreshLocation,
   } = useUserLocation();

   const requestLocationAccess = async () => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
         toast.error("Geolocation is not supported by this browser.");
         return;
      }

      if (navigator.permissions) {
         try {
            const permissionStatus = await navigator.permissions.query({
               name: "geolocation",
            });

            if (permissionStatus.state === "denied") {
               toast(
                  (t) => (
                     <div className="location-toast">
                        <div className="location-toast-icon">
                           <i className="fa fa-map-marker" aria-hidden="true" />
                        </div>

                        <div className="location-toast-content">
                           <h2 className="location-toast-title">
                              Location Access Blocked
                           </h2>

                           <p className="location-toast-description">
                              To enable live location, click the lock or
                              site-info icon in your browser address bar,
                              allow Location, then refresh this page.
                           </p>

                           <div className="location-toast-actions">
                              <button
                                 className="location-toast-btn primary"
                                 onClick={() => toast.dismiss(t.id)}
                              >
                                 Got it
                              </button>
                           </div>
                        </div>
                     </div>
                  ),
                  {
                     id: "location-permission-denied",
                     duration: Infinity,
                     style: {
                        background: "transparent",
                        border: "none",
                        boxShadow: "none",
                        padding: 0,
                     },
                  }
               );

               return;
            }
         } catch (error) {
            console.warn(
               "Could not read geolocation permission state",
               error
            );
         }
      }

      refreshLocation();
   };

   // clubs in spotlight params
   const clubsInSpotlightParams = new URLSearchParams();
   clubsInSpotlightParams.append("view", 'clubs-spotlight');
   clubsInSpotlightParams.append("isSpotlighted", true);
   clubsInSpotlightParams.append("ordered", 1);
   clubsInSpotlightParams.append("city", selectedCity);
   clubsInSpotlightParams.append("userBaseCity", userBaseCity);

   const clubsInSpotlightUrl = `api/clubs?${clubsInSpotlightParams}`;
   console.info("Fetching clubs in spotlight", clubsInSpotlightUrl)

   const {
      data: clubsInSpotlightData,
      error: clubsInSpotlightError,
      isLoading: clubsInSpotlightIsLoading,
      isError: clubsInSpotlightIsError,
      refetch: clubsInSpotlightRefetch,
   } = useQuery({ queryKey: [clubsInSpotlightUrl] });





   return (
      <Layout>
         {/* Hero Start */}
         <div className="homepage">
            {import.meta.env?.VITE_HOME_HERO_IMG_URL ? (
               <Banner
                  url={import.meta.env.VITE_HOME_HERO_CTA_URL}
                  src={import.meta.env.VITE_HOME_HERO_IMG_URL}
                  srcMobile={import.meta.env.VITE_HOME_HERO_IMG_SM_URL}>
               </Banner>
            ) : (
               <VideoBanner  >
                  <source src={isMobile ? 'https://api.azzirevents.com/storage/azzir-intro@sm.mp4' : 'https://api.azzirevents.com/storage/azzir-intro.mp4'} />
               </VideoBanner>
            )}
            <HeroCtaForm />
         </div>
         {/* Hero End */}

         <PageSeoContent variant="home" />

         {/* About Start */}

         {/* About End */}

         {/* Club-Spotlight Start */}
         <div
            className="club-spotlight head-title  section-gap wow fadeInUp"
            data-wow-delay="0.2s"
         >
            <div className="container">
               <div className="mb-4 title-sec wow fadeInUp" data-wow-delay="0.3s">
                  <h2 className="title-25">Clubs in Spotlight</h2>
               </div>
               <div className="px-3 mb-2 text-center wow fadeInUp" data-wow-delay="0.4s">
                  <strong>Curated Nightlife Gems, Just for You</strong>
               </div>
               <div className="px-3 text-center wow fadeInUp" data-wow-delay="0.4s">
                  Discover the most talked-about clubs lighting up the scene. Whether it’s their vibe, venue, or the energy they bring, these hotspots have earned their place in the spotlight. Dive in, explore, and find your next unforgettable night out.
               </div>
            </div>
            <div className="club-items wow fadeInUp" data-wow-delay="0.5s">
               <div
                  id="club-spotlight-slider"
                  className="club-result-row itmes-slider"
               >
                  {clubsInSpotlightData?.data?.length > 0 ? (
                     <>
                        <div className="container">
                           <div className="container__content">
                              <ErrorBoundary>
                                 <ClubsCarousel data={clubsInSpotlightData?.data} />
                              </ErrorBoundary>
                           </div>
                        </div>
                     </>
                  ) : (
                     <div className="no-data">
                        <p className="no-data-found">No clubs found</p>
                     </div>
                  )}
               </div>
            </div>
         </div>
         {/* Club-Sportlight End */}

         <ClubsSection
            title={'Clubs Nearby'}
            view={'nearby-clubs'}
            coords={coords}
            city={locationData?.city || null}
            baseCity={locationData?.city || null}
            locationData={locationData}
            locationStatus={locationStatus}
            locationPermission={permissionState}
            locationLoading={locationLoading}
            locationError={locationError}
            onRetryLocation={requestLocationAccess}
         />

         {/* Club-Sportlight Start */}
         <ArtistsSection title={'Artists in Spotlight'} city={selectedCity} baseCity={userBaseCity} />
         {/* Club-Sportlight End */}

         {/* Nearby events Start */}
         <EventsSection onOpenForm={() => setEventModalOpen(prev => !prev)} title={'Events in Spotlight'} view={'events-spotlight'} coords={isManualCitySelection ? null : coords} city={selectedCity} baseCity={userBaseCity} />
         <EventSubmitForm isOpen={eventModalOpen} closeModal={() => setEventModalOpen(false)} />
         {/* Service Start */}
         <div className="service">
            <div className="container">
               <div
                  className="title-sec mb-5 text-center wow zoomIn"
                  data-wow-delay="0.1s"
               >
                  {/* <h2 className="title-45 white-text">
              A culinary adventure for all the senses
            </h2> */}
                  <p className="p-20 white-text">
                     From hidden gems to exclusive parties, Azzir Nightlife brings the best of the night straight to your fingertips.
                  </p>
               </div>
               <div className="row">
                  <div
                     className="col-lg-4 col-md-6 wow fadeInUp"
                     data-wow-delay="0.0s"
                  >
                     <div className="service-item">
                        <Link to="/events">
                           <div className="service-icon">
                              <svg
                                 width="32"
                                 height="34"
                                 viewBox="0 0 32 34"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    d="M29.3333 2.61538H25.3333V1.30769C25.3333 0.960871 25.1929 0.628254 24.9428 0.383014C24.6928 0.137774 24.3536 0 24 0C23.6464 0 23.3072 0.137774 23.0572 0.383014C22.8071 0.628254 22.6667 0.960871 22.6667 1.30769V2.61538H9.33333V1.30769C9.33333 0.960871 9.19286 0.628254 8.94281 0.383014C8.69276 0.137774 8.35362 0 8 0C7.64638 0 7.30724 0.137774 7.05719 0.383014C6.80714 0.628254 6.66667 0.960871 6.66667 1.30769V2.61538H2.66667C1.95942 2.61538 1.28115 2.89093 0.781048 3.38141C0.280951 3.87189 0 4.53713 0 5.23077V31.3846C0 32.0783 0.280951 32.7435 0.781048 33.234C1.28115 33.7244 1.95942 34 2.66667 34H29.3333C30.0406 34 30.7189 33.7244 31.219 33.234C31.719 32.7435 32 32.0783 32 31.3846V5.23077C32 4.53713 31.719 3.87189 31.219 3.38141C30.7189 2.89093 30.0406 2.61538 29.3333 2.61538ZM6.66667 5.23077V6.53846C6.66667 6.88528 6.80714 7.2179 7.05719 7.46314C7.30724 7.70838 7.64638 7.84615 8 7.84615C8.35362 7.84615 8.69276 7.70838 8.94281 7.46314C9.19286 7.2179 9.33333 6.88528 9.33333 6.53846V5.23077H22.6667V6.53846C22.6667 6.88528 22.8071 7.2179 23.0572 7.46314C23.3072 7.70838 23.6464 7.84615 24 7.84615C24.3536 7.84615 24.6928 7.70838 24.9428 7.46314C25.1929 7.2179 25.3333 6.88528 25.3333 6.53846V5.23077H29.3333V10.4615H2.66667V5.23077H6.66667ZM29.3333 31.3846H2.66667V13.0769H29.3333V31.3846ZM13.3333 17V27.4615C13.3333 27.8084 13.1929 28.141 12.9428 28.3862C12.6928 28.6315 12.3536 28.7692 12 28.7692C11.6464 28.7692 11.3072 28.6315 11.0572 28.3862C10.8071 28.141 10.6667 27.8084 10.6667 27.4615V19.1152L9.93 19.4781C9.61351 19.6333 9.24712 19.6588 8.91143 19.5491C8.57574 19.4393 8.29825 19.2033 8.14 18.8929C7.98175 18.5825 7.95572 18.2231 8.06761 17.8939C8.17951 17.5647 8.42017 17.2925 8.73667 17.1373L11.4033 15.8296C11.6067 15.7298 11.8327 15.6827 12.0599 15.6927C12.287 15.7027 12.5078 15.7695 12.7012 15.8868C12.8946 16.0041 13.0542 16.1679 13.1649 16.3628C13.2755 16.5576 13.3335 16.777 13.3333 17ZM23.1933 21.9774L20 26.1538H22.6667C23.0203 26.1538 23.3594 26.2916 23.6095 26.5369C23.8595 26.7821 24 27.1147 24 27.4615C24 27.8084 23.8595 28.141 23.6095 28.3862C23.3594 28.6315 23.0203 28.7692 22.6667 28.7692H17.3333C17.0857 28.7692 16.843 28.7016 16.6324 28.5739C16.4217 28.4463 16.2515 28.2636 16.1408 28.0464C16.03 27.8291 15.9831 27.586 16.0054 27.3441C16.0276 27.1022 16.1181 26.8712 16.2667 26.6769L21.0633 20.4049C21.1724 20.2624 21.2507 20.0996 21.2933 19.9264C21.336 19.7532 21.3421 19.5733 21.3113 19.3978C21.2805 19.2222 21.2134 19.0546 21.1142 18.9054C21.015 18.7561 20.8857 18.6283 20.7344 18.5298C20.583 18.4313 20.4127 18.3642 20.2339 18.3326C20.0551 18.301 19.8716 18.3055 19.6947 18.3459C19.5178 18.3864 19.3512 18.4618 19.205 18.5677C19.0589 18.6735 18.9364 18.8076 18.845 18.9615C18.76 19.115 18.6446 19.2502 18.5055 19.3592C18.3664 19.4682 18.2064 19.5488 18.035 19.5961C17.8637 19.6435 17.6844 19.6568 17.5077 19.6351C17.331 19.6134 17.1606 19.5572 17.0064 19.4699C16.8522 19.3825 16.7174 19.2658 16.61 19.1265C16.5026 18.9873 16.4247 18.8283 16.3809 18.6591C16.3371 18.4898 16.3283 18.3136 16.3551 18.141C16.3818 17.9683 16.4436 17.8027 16.5367 17.6538C16.9771 16.9063 17.6567 16.3222 18.47 15.9919C19.2834 15.6617 20.1851 15.6038 21.0354 15.8273C21.8857 16.0508 22.637 16.5431 23.173 17.228C23.709 17.9129 23.9997 18.752 24 19.6154C24.0028 20.4682 23.7194 21.2983 23.1933 21.9774Z"
                                    fill="#C12159"
                                 />
                              </svg>
                           </div>
                           <h3 className="title-24">Find What’s Happening — Before It Happens</h3>
                           <p className="p-16">
                              Stay ahead with curated event listings tailored to your tastes and location. From underground raves to rooftop sets, the scene is always just a tap away.
                           </p>
                        </Link>
                     </div>
                  </div>
                  <div
                     className="col-lg-4 col-md-6 wow fadeInUp"
                     data-wow-delay="0.2s"
                  >
                     <div className="service-item">
                        <Link to="/artists">
                           <div className="service-icon">
                              <svg
                                 width="35"
                                 height="35"
                                 viewBox="0 0 35 35"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    d="M30.9647 0.0562134C30.8927 0.0832253 30.7666 0.164261 30.6856 0.227287C30.5686 0.335335 30.5055 0.335335 30.2714 0.218285C29.7041 -0.060833 29.2269 0.443378 29.5421 0.992615C29.6771 1.22672 29.6681 1.24472 29.416 1.50584C29.1549 1.75795 29.1369 1.76695 28.9028 1.63189C28.5786 1.45181 28.2004 1.55086 28.0654 1.84798C27.9753 2.04607 27.9843 2.1361 28.1104 2.3612C28.2725 2.63132 28.2725 2.64032 28.0023 2.90144C27.7412 3.17155 27.7322 3.17155 27.4441 3.03649C27.282 2.95546 27.0929 2.91044 27.0119 2.93745C26.6247 3.07251 26.5256 3.59473 26.8318 3.91887L27.0119 4.11695L26.5616 4.99933L26.1114 5.89072L23.4912 8.4118C22.0415 9.79839 20.6908 11.0859 20.4747 11.275L20.0785 11.6172L19.2501 11.113C16.819 9.64533 14.3968 9.8254 12.5869 11.6082C11.9837 12.2024 11.9476 12.2654 10.9932 14.3183C10.3539 15.7049 9.80461 16.1281 8.18383 16.4703C5.14938 17.1185 3.82575 17.6678 2.55614 18.8383C1.54766 19.7567 0.593198 21.2513 0.215017 22.5028C-0.442298 24.7088 0.431121 27.599 2.51112 30.0481C5.77068 33.9017 9.38141 35.6305 12.4609 34.7931C13.6765 34.469 15.2252 33.4875 16.1707 32.4521C17.3412 31.1826 17.8905 29.859 18.5388 26.8247C18.917 25.0329 19.3852 24.5107 21.3211 23.7184C22.3926 23.2772 23.068 22.836 23.5722 22.2687C24.4636 21.2603 24.8598 20.2159 24.8598 18.9013C24.8598 17.8028 24.5807 16.9024 23.8964 15.7589L23.3921 14.9306L23.6442 14.6335C23.7793 14.4714 25.0759 13.1118 26.5076 11.6172L29.1189 8.89801L30.0103 8.44781L30.8927 7.99762L31.0908 8.1777C31.595 8.6549 32.2794 8.1867 31.9822 7.56544C31.8382 7.27731 31.8382 7.26831 32.1083 7.00719C32.3694 6.73708 32.3784 6.73708 32.6486 6.89915C32.8737 7.0252 32.9637 7.03421 33.1618 6.94417C33.4589 6.80911 33.558 6.43095 33.3689 6.12482C33.2338 5.89972 33.2338 5.88171 33.504 5.6116C33.7651 5.33248 33.7831 5.33248 34.0172 5.46754C34.5665 5.78267 35.0707 5.30546 34.7826 4.75623C34.6565 4.50412 34.6565 4.46811 34.8006 4.30604C35.0257 4.05393 35.0707 3.32462 34.8816 2.64032C34.5845 1.53285 33.594 0.506409 32.4865 0.164261C31.9642 0.00219345 31.2529 -0.0518341 30.9647 0.0562134ZM32.4505 1.35277C33.2428 1.76695 33.8461 2.61331 33.8461 3.2886C33.8461 3.63075 33.7831 3.71178 32.1623 5.31447C30.6046 6.83612 30.4065 7.0072 29.7221 7.33134L28.9748 7.68249L28.1554 6.85413L27.327 6.03478L27.6782 5.28746C28.0023 4.60316 28.1734 4.40508 29.6951 2.84741C31.3159 1.20871 31.3699 1.16369 31.7301 1.16369C31.9282 1.16369 32.2524 1.24472 32.4505 1.35277ZM27.354 7.68249C27.7142 8.05164 28.0023 8.39379 27.9843 8.44781C27.9663 8.49283 27.4351 9.06007 26.8138 9.69035C26.1925 10.3206 24.1485 12.4455 22.2666 14.3994C20.3937 16.3532 18.8179 17.9649 18.7639 17.9829C18.7099 17.9919 18.5928 17.8839 18.5028 17.7218C18.2326 17.2626 17.8184 16.8394 17.3952 16.5873C17.1791 16.4613 17.0081 16.3082 17.0261 16.2542C17.0531 16.1551 17.7194 15.5068 21.8254 11.5631C23.068 10.3746 24.6257 8.87099 25.292 8.21371C25.9583 7.55643 26.5526 7.0162 26.5977 7.0162C26.6517 7.0162 26.9938 7.31333 27.354 7.68249ZM17.0531 11.3831C17.6023 11.5181 18.6829 12.0223 19.061 12.3105L19.2591 12.4635L17.4493 14.2193L15.6304 15.966L15.0361 16.0291C13.2172 16.2271 11.8756 17.7218 11.8756 19.5406C11.8756 21.5754 13.4243 23.1331 15.4413 23.1331C17.2782 23.1331 18.7819 21.7915 18.98 19.9818L19.043 19.3785L20.7899 17.5597L22.5457 15.7499L22.6988 15.948C22.9959 16.3442 23.4912 17.4157 23.6352 18.0009C23.9684 19.3155 23.5452 20.7381 22.5547 21.6745C22.0415 22.1607 21.7623 22.3228 20.6548 22.773C19.3492 23.3132 18.5388 23.9435 17.9805 24.8438C17.7734 25.186 17.5753 25.8523 17.3142 27.0948C16.846 29.3638 16.2787 30.6603 15.3152 31.6958C14.4058 32.6772 13.1992 33.4245 12.0377 33.7217C11.1102 33.9648 9.79561 33.8567 8.75111 33.4695C6.40098 32.5872 3.76272 30.2552 2.24999 27.7341C1.55666 26.5906 1.24151 25.6182 1.18748 24.4837C1.13346 23.3762 1.26852 22.7009 1.74575 21.7645C2.8983 19.4956 4.5641 18.3881 7.9137 17.6948C9.38141 17.3886 9.84063 17.2266 10.4079 16.8574C11.1733 16.3532 11.7676 15.5429 12.2358 14.3543C12.794 12.9227 13.4333 12.1484 14.4418 11.6442C15.3062 11.221 16.1076 11.14 17.0531 11.3831ZM16.1977 17.2446C16.882 17.4517 17.5753 18.145 17.7734 18.8563C18.1876 20.2699 17.3952 21.6115 15.9456 21.9626C15.4143 22.0887 15.0001 22.0346 14.3968 21.7735C13.6765 21.4584 13.2623 20.9272 13.0372 20.0358C12.758 18.9103 13.5774 17.5417 14.73 17.2266C15.3152 17.0735 15.6304 17.0735 16.1977 17.2446Z"
                                    fill="#C12159"
                                 />
                                 <path
                                    d="M5.33521 23.0626C5.07887 23.2 5 23.3275 5 23.6219C5 23.9948 11.0535 30 11.4282 30C11.7732 30 12 29.7645 12 29.3916C12 29.1267 11.6155 28.7146 8.97324 26.075C7.29718 24.4167 5.84789 23.043 5.72958 23.0135C5.61127 22.9841 5.4338 23.0037 5.33521 23.0626Z"
                                    fill="#C12159"
                                 />
                              </svg>
                           </div>
                           <h3 className="title-24">Know Who’s Playing Where</h3>
                           <p className="p-16">
                              Track performances, explore artist bios, and follow your favorite names. Whether it’s techno, hip-hop, or house, know exactly where your sound lives.
                           </p>
                        </Link>
                     </div>
                  </div>
                  <div
                     className="col-lg-4 col-md-6 wow fadeInUp"
                     data-wow-delay="0.4s"
                  >
                     <div className="service-item">
                        <Link to="/clubs">
                           <div className="service-icon">
                              <svg
                                 width="35"
                                 height="35"
                                 viewBox="0 0 35 35"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                                 xlinkHref="http://www.w3.org/1999/xlink"
                              >
                                 <mask
                                    id="mask0_53_122"
                                    style={{ maskType: "alpha" }}
                                    maskUnits="userSpaceOnUse"
                                    x="0"
                                    y="0"
                                    width="35"
                                    height="35"
                                 >
                                    <rect width="35" height="35" fill="url(#pattern0)" />
                                 </mask>
                                 <g mask="url(#mask0_53_122)">
                                    <rect width="35" height="35" fill="#C12159" />
                                 </g>
                                 <defs>
                                    <pattern
                                       id="pattern0"
                                       patternContentUnits="objectBoundingBox"
                                       width="1"
                                       height="1"
                                    >
                                       <use
                                          xlinkHref="#image0_53_122"
                                          transform="scale(0.00195312)"
                                       />
                                    </pattern>
                                    <image
                                       id="image0_53_122"
                                       width="512"
                                       height="512"
                                       xlinkHref="data:image/png;base64,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"
                                    />
                                 </defs>
                              </svg>
                           </div>
                           <h3 className="title-24">Vibes That Match Your Mood</h3>
                           <p className="p-16">
                              Looking for upscale, chill, or high-energy? Azzir helps you zero in on the perfect venue — based on your style, preferences, and location.
                           </p>
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
         </div>
         {/* Service End */}
         <Link to="#" className="back-to-top">
            <i className="fa fa-chevron-up"></i>
         </Link>
      </Layout>
   );
}

function EventsSection({ title, view, city, baseCity, coords, onOpenForm }) {
   // console.log(clubsNearby,'clubsNearbyData')
   const queryParams = new URLSearchParams();
   queryParams.append("upcoming", 1);
   queryParams.append("isSpotlighted", true);
   queryParams.append("ordered", 1);
   queryParams.append("page", 0);
   queryParams.append("view", view);
   queryParams.append("sort_by", 'eventStartTime');
   queryParams.append("sort_dir", 'asc');
   queryParams.append("city", city);
   queryParams.append("userBaseCity", baseCity);
   if (coords) {
      // alert(`coordinates-,${coords.latitude},${coords.longitude}`);
      queryParams.append(
         "coordinates",
         `${coords.latitude},${coords.longitude}` //19.0596, 72.8295
      );
   }

   const url = `/api/events?${queryParams}`;
   console.info('Fetching events', view, url)

   const {
      data: upcomingEventsData,
      error: upcomingEventsDataError,
      isLoading: upcomingEventsDataIsLoading,
      isError: upcomingEventsDataIsError,
      refetch: upcomingEventsRefetch,
      isFetching: upcomingEventsIsFetching,
   } = useQuery({
      queryKey: [url],
      select(res) {
         console.log('res', res, 'sorting by eventStartTime')

         const sortedData = res?.data?.slice().sort((a, b) => {
            const dateA = moment(a.eventStartTime);
            const dateB = moment(b.eventStartTime);
            return dateA.diff(dateB);
         }) || [];

         console.log('sortedData after sorting', sortedData);

         if (sortedData?.length <= 3) {
            if (sortedData?.length < 2) {
               return [
                  ...sortedData,
                  ...sortedData,
                  ...sortedData
               ]
            }
            return [
               ...sortedData,
               ...sortedData
            ]
         }
         return sortedData
      }
   });
   return (
      <div
         className="club-spotlight head-title section-gap wow fadeInUp"
         data-wow-delay="0.2s"
      >
         <div className="container">
            <div className="mb-4 title-sec wow fadeInUp" data-wow-delay="0.3s">
               <h2 className="title-25">{title}</h2>
            </div>
            <div className="px-3 mb-2 text-center wow fadeInUp" data-wow-delay="0.4s">
               <strong>Unmissable Nights, All in One Place</strong>
            </div>
            <div className="px-3 text-center wow fadeInUp" data-wow-delay="0.4s">
               We highlight the most anticipated events so you don’t miss a beat. From exclusive performances to club takeovers, these experiences are front and center — and filling fast.
            </div>
         </div>
         <div className="container">
            <div className="carousel-section--home-events container__content mb-4">
               <div className="club-items wow fadeInUp" data-wow-delay="0.5s">
                  <div
                     id="club-events-slider"
                     className="club-result-row itmes-slider card-list mb-4"
                  >
                     {upcomingEventsData?.length > 0 ? (
                        <>
                           <EventsGallery data={upcomingEventsData} />
                        </>
                     ) : (
                        <div className="no-data">
                           <p className="no-data-found">No events found</p>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         </div>
         {import.meta.env?.VITE_ENABLE_LISTEVENTFORM ? (
            <div className="mt-4 pt-4 text-center mb-4 container">
               <picture>
                  <source media="(max-width: 768px)" srcSet={listEventCtaMobileImg} />
                  <ImageWithSkeleton
                     src={listEventCtaImg}
                     alt="List event call to action"
                     className="img-fluid cursor-pointer btn"
                     wrapperClassName="list-event-cta-wrapper"
                     skeletonHeight={220}
                     onClick={onOpenForm}
                  />
               </picture>
               {/* <button type="button" onClick={onOpenForm} className="btn btn-dark btn-black btn--dark">List Your Event</button> */}
            </div>
         ) : null}
      </div>
   )
}

function ArtistsSection({ title, view = "spotlight-artists", city, baseCity }) {
   // artists in spotlight api params
   const artistsInSpotlightParams = new URLSearchParams();
   artistsInSpotlightParams.append("isSpotlighted", true);
   artistsInSpotlightParams.append("ordered", 1);
   artistsInSpotlightParams.append("city", city);
   artistsInSpotlightParams.append("userBaseCity", baseCity);
   artistsInSpotlightParams.append("view", view);

   const artistsInSpotlightUrl = `api/artists?${artistsInSpotlightParams}`;
   console.info('Fetching artists in spotlight', artistsInSpotlightUrl)
   const {
      data: artistsInSpotlightData,
      error: artistsInSpotlightError,
      isLoading: artistsInSpotlightIsLoading,
      isError: artistsInSpotlightIsError,
      refetch: artistsInSpotlightRefetch,
   } = useQuery({
      queryKey: [artistsInSpotlightUrl],
      placeholderData: [{}, {}, {}, {}],
   });

   return (
      <div
         className="club-spotlight head-title club-nearby section-gap wow fadeInUp"
         data-wow-delay="0.2s"
      >
         <div className="container">
            <div className="mb-4 title-sec wow fadeInUp" data-wow-delay="0.3s">
               <h2 className="title-25">{title}</h2>
            </div>
            <div className="px-3 mb-2 text-center wow fadeInUp" data-wow-delay="0.4s">
               <strong>The Talent Turning Nights into Memories</strong>
            </div>
            <div className="px-3 text-center wow fadeInUp" data-wow-delay="0.4s">
               From chart-topping DJs to underground sensations to mesmerizing singers, these artists are making waves in the nightlife scene. See who’s trending, where they’re performing next, and why they’re the name on everyone’s lips.
            </div>
         </div>
         <div className="club-items wow fadeInUp" data-wow-delay="0.5s">
            <div id="club-aritst-slider" className="club-result-row itmes-slider">
               {artistsInSpotlightData?.data?.length ? (
                  <>
                     <div className="container">
                        <div className="container__content">
                           <ErrorBoundary>
                              <ArtistsCarousel data={artistsInSpotlightData?.data} slides={artistsInSpotlightData?.data} />
                           </ErrorBoundary>
                        </div>
                     </div>
                  </>
               ) : (
                  <div className="no-data">
                     <p className="no-data-found">No artists found</p>
                  </div>
               )}
            </div>
         </div>
      </div>
   );
}
